import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const PanelTitle = styled.h2`
  color: #2D3653;
  text-align: center;
  font-family: 'Style Script', cursive;
  font-size: clamp(16px, 1.5vw, 24px);
  margin-bottom: 10px;
  margin-top: 10px;

  @media (max-width: 768px) {
    margin-top: 22px;
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  color: #2D3653;
  text-align: center;
  font-family: 'Style Script', cursive;
  font-size: clamp(16px, 1.5vw, 24px);
  margin: 5px 0;
  position: relative;
  
  &::before, &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background: #2D3653;
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
`;

const TimeTable = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$displayRoom ? '4fr 2fr 2fr' : '4fr 1fr'};
  gap: 10px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(45, 54, 83, 0.2);
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const TimeLabel = styled.div`
  color: #2D3653;
  font-family: 'IBM Plex Sans JP', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${props => props.$fontSize ? `clamp(${props.$fontSize}px, 1vw, ${props.$fontSize}px)` : 'clamp(14px, 1vw, 18px)'};
`;

const TimeValue = styled.div`
  color: #2D3653;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${props => props.$fontSize ? `clamp(${props.$fontSize}px, 1vw, ${props.$fontSize}px)` : 'clamp(14px, 1vw, 18px)'};
`;

const ContentWrapper = styled.div`
  overflow: hidden;
`;

const WeekScheduleContainer = ({ 
  weekSchedule,  // Maintenant on reçoit directement le schedule complet
  isTransitioning,
  AnimatedContent,
  displayRoom = true,
  fontSize
}) => {
  const { t } = useTranslation();

  // Grouper les horaires par section et trier
  const groupedSchedule = weekSchedule.reduce((acc, item) => {
    const section = item.sectionName || 'default';
    if (!acc[section]) {
      acc[section] = {
        name: section,
        rank: item.rank !== undefined ? item.rank : 999,
        items: []
      };
    }
    acc[section].items.push(item);
    return acc;
  }, {});

  // Trier les sections par rank
  const sortedSections = Object.values(groupedSchedule)
    .sort((a, b) => {
      return (a.rank !== undefined ? a.rank : 999) - (b.rank !== undefined ? b.rank : 999);
    })
    .map(section => {
      const sortedItems = section.items.sort((a, b) => {
        if (a.rank !== b.rank) {
          return (a.rank !== undefined ? a.rank : 999) - (b.rank !== undefined ? b.rank : 999);
        }
        return a.time.localeCompare(b.time);
      });

      return {
        sectionName: section.name,
        items: sortedItems
      };
    });

  return (
    <>
      <PanelTitle>{t('display.weekSchedule')}</PanelTitle>
      <ContentWrapper>
        {isTransitioning === undefined ? (
          <div>
            {sortedSections.map(({ sectionName, items }) => (
              <React.Fragment key={`section-${sectionName}`}>
                <SectionTitle>{sectionName}</SectionTitle>
                {items.map((item, index) => (
                  <TimeTable key={`${sectionName}-${index}`} $displayRoom={displayRoom}>
                    <TimeLabel $fontSize={fontSize}>{item.name}</TimeLabel>
                    {displayRoom && <TimeLabel $fontSize={fontSize}>{item.roomName}</TimeLabel>}
                    <TimeValue $fontSize={fontSize}>{item.time}</TimeValue>
                  </TimeTable>
                ))}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <AnimatedContent $isEntering={!isTransitioning}>
            {sortedSections.map(({ sectionName, items }) => (
              <React.Fragment key={`section-${sectionName}`}>
                <SectionTitle>{sectionName}</SectionTitle>
                {items.map((item, index) => (
                  <TimeTable key={`${sectionName}-${index}`} $displayRoom={displayRoom}>
                    <TimeLabel $fontSize={fontSize}>{item.name}</TimeLabel>
                    {displayRoom && <TimeLabel $fontSize={fontSize}>{item.roomName}</TimeLabel>}
                    <TimeValue $fontSize={fontSize}>{item.time}</TimeValue>
                  </TimeTable>
                ))}
              </React.Fragment>
            ))}
          </AnimatedContent>
        )}
      </ContentWrapper>
    </>
  );
};

export default WeekScheduleContainer; 